/**
 * INTAKE TEMPLATES
 * 
 * Structured input forms for Bug, Feature, and Refactor requests.
 * Implements the "templates that enforce input" pattern from research findings.
 */

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Bug,
  Sparkles,
  Wrench,
  FileCode,
  CheckCircle,
  X,
  Plus,
} from "lucide-react";
import { useWorkflowStore } from "@/stores/workflowStore";
import {
  getRequiredStartFields,
  isStartFieldRequired,
  normalizeWorkflowIssueType,
  type StartFieldKey,
} from "@/lib/workflow/startFields";

type TemplateType = "bug" | "feature" | "refactor";
type RiskLevel = "low" | "medium" | "high";

const FALLBACK_WORKFLOW_IDS = new Set(["wf-bug-default", "wf-feature-default"]);
const FALLBACK_WORKFLOW_LABELS: Record<string, string> = {
  "wf-bug-default": "Bug Fix (Default)",
  "wf-feature-default": "Feature (Default)",
};
const TEMPLATE_LABELS: Record<TemplateType, string> = {
  bug: "Bug",
  feature: "Feature",
  refactor: "Refactor",
};

function getWorkflowDisplayName(workflow: { id: string; name: string }): string {
  return FALLBACK_WORKFLOW_LABELS[workflow.id] ?? workflow.name;
}

interface BugReport {
  projectId: number;
  title: string;
  description: string;
  stepsToReproduce: string[];
  expectedBehavior: string;
  actualBehavior: string;
  affectedFiles?: string[];
  severity: RiskLevel;
}

interface FeatureRequest {
  projectId: number;
  title: string;
  description: string;
  userStory: string;
  acceptanceCriteria: string[];
  scope: RiskLevel;
  constraints?: string;
}

interface RefactorRequest {
  projectId: number;
  title: string;
  description: string;
  motivation: string;
  targetFiles: string[];
  approach: string;
  riskLevel: RiskLevel;
}

interface IntakeTemplatesProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    type: TemplateType,
    data: BugReport | FeatureRequest | RefactorRequest,
    context?: {
      workflowId?: string;
      workflowName?: string;
      workflowIssueType?: string;
      requiredStartFields?: StartFieldKey[];
    },
  ) => void;
  defaultType?: TemplateType;
  projects?: { id: number; name: string }[];
}

export function IntakeTemplates({ isOpen, onClose, onSubmit, defaultType = "bug", projects = [] }: IntakeTemplatesProps) {
  const [templateType, setTemplateType] = useState<TemplateType>(defaultType);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>("");
  const workflows = useWorkflowStore((s) => s.workflows);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);

  useEffect(() => {
    if (isOpen) {
      void fetchWorkflows();
    }
  }, [isOpen, fetchWorkflows]);

  const savedWorkflows = useMemo(() => {
    const custom = workflows.filter((workflow) => !FALLBACK_WORKFLOW_IDS.has(workflow.id));
    const source = custom.length > 0 ? custom : workflows;
    return [...source].sort((a, b) => {
      const aTs = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTs = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bTs - aTs;
    });
  }, [workflows]);
  const selectedWorkflow = useMemo(
    () => savedWorkflows.find((workflow) => workflow.id === selectedWorkflowId),
    [savedWorkflows, selectedWorkflowId],
  );
  const selectedWorkflowDisplayName = useMemo(
    () => (selectedWorkflow ? getWorkflowDisplayName(selectedWorkflow) : ""),
    [selectedWorkflow],
  );
  const selectedStartNode = useMemo(
    () => selectedWorkflow?.nodes.find((node) => node.type === "start"),
    [selectedWorkflow],
  );
  const requiredStartFields = useMemo(
    () =>
      getRequiredStartFields(
        selectedStartNode?.config?.startRequiredFields,
        selectedWorkflow?.issueType,
      ),
    [selectedStartNode, selectedWorkflow],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (savedWorkflows.length === 0) {
      setSelectedWorkflowId("");
      return;
    }

    const existing = savedWorkflows.find((workflow) => workflow.id === selectedWorkflowId);
    const workflowToSelect = existing ?? savedWorkflows[0];
    setSelectedWorkflowId(workflowToSelect.id);
    setTemplateType(normalizeWorkflowIssueType(workflowToSelect.issueType));
  }, [isOpen, savedWorkflows, selectedWorkflowId]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>
            Select a saved workflow and provide the required information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mb-4">
          <Label>Workflow *</Label>
          <Select
            value={selectedWorkflowId}
            onValueChange={(workflowId) => {
              setSelectedWorkflowId(workflowId);
              const workflow = savedWorkflows.find((w) => w.id === workflowId);
              if (workflow) {
                setTemplateType(normalizeWorkflowIssueType(workflow.issueType));
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select saved workflow..." />
            </SelectTrigger>
            <SelectContent>
              {savedWorkflows.map((workflow) => (
                <SelectItem key={workflow.id} value={workflow.id}>
                  {getWorkflowDisplayName(workflow)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedWorkflow ? (
            <p className="text-xs text-muted-foreground">
              Type: {TEMPLATE_LABELS[templateType]}
            </p>
          ) : (
            <p className="text-xs text-amber-500">
              No saved workflows found. Create and save one first in Agent Builder.
            </p>
          )}
        </div>

        {selectedWorkflow && templateType === "bug" && (
          <BugReportForm 
            onSubmit={(data) =>
              onSubmit("bug", data, {
                workflowId: selectedWorkflow.id,
                workflowName: selectedWorkflowDisplayName,
                workflowIssueType: selectedWorkflow.issueType,
                requiredStartFields,
              })
            }
            onClose={onClose} 
            projects={projects}
            selectedProject={selectedProject}
            onProjectChange={setSelectedProject}
            requiredStartFields={requiredStartFields}
          />
        )}
        {selectedWorkflow && templateType === "feature" && (
          <FeatureRequestForm 
            onSubmit={(data) =>
              onSubmit("feature", data, {
                workflowId: selectedWorkflow.id,
                workflowName: selectedWorkflowDisplayName,
                workflowIssueType: selectedWorkflow.issueType,
                requiredStartFields,
              })
            }
            onClose={onClose}
            projects={projects}
            selectedProject={selectedProject}
            onProjectChange={setSelectedProject}
            requiredStartFields={requiredStartFields}
          />
        )}
        {selectedWorkflow && templateType === "refactor" && (
          <RefactorRequestForm 
            onSubmit={(data) =>
              onSubmit("refactor", data, {
                workflowId: selectedWorkflow.id,
                workflowName: selectedWorkflowDisplayName,
                workflowIssueType: selectedWorkflow.issueType,
                requiredStartFields,
              })
            }
            onClose={onClose}
            projects={projects}
            selectedProject={selectedProject}
            onProjectChange={setSelectedProject}
            requiredStartFields={requiredStartFields}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface FormProps {
  onClose: () => void;
  projects: { id: number; name: string }[];
  selectedProject: string;
  onProjectChange: (value: string) => void;
  requiredStartFields: StartFieldKey[];
}

function fieldRequired(requiredStartFields: StartFieldKey[], field: StartFieldKey): boolean {
  return isStartFieldRequired(requiredStartFields, field);
}

function BugReportForm({
  onSubmit,
  onClose,
  projects,
  selectedProject,
  onProjectChange,
  requiredStartFields,
}: FormProps & { onSubmit: (data: BugReport) => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState<string[]>([""]);
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [actualBehavior, setActualBehavior] = useState("");
  const [severity, setSeverity] = useState<RiskLevel>("medium");

  const addStep = () => setStepsToReproduce([...stepsToReproduce, ""]);
  const removeStep = (index: number) => setStepsToReproduce(stepsToReproduce.filter((_, i) => i !== index));
  const updateStep = (index: number, value: string) => {
    const newSteps = [...stepsToReproduce];
    newSteps[index] = value;
    setStepsToReproduce(newSteps);
  };

  const isValid =
    !!selectedProject &&
    (!fieldRequired(requiredStartFields, "title") || !!title.trim()) &&
    (!fieldRequired(requiredStartFields, "description") || !!description.trim()) &&
    (!fieldRequired(requiredStartFields, "stepsToReproduce") || stepsToReproduce.some((s) => s.trim())) &&
    (!fieldRequired(requiredStartFields, "expectedBehavior") || !!expectedBehavior.trim()) &&
    (!fieldRequired(requiredStartFields, "actualBehavior") || !!actualBehavior.trim()) &&
    (!fieldRequired(requiredStartFields, "severity") || !!severity);

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      title,
      description,
      stepsToReproduce: stepsToReproduce.filter(s => s.trim()),
      expectedBehavior,
      actualBehavior,
      severity,
      projectId: parseInt(selectedProject),
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Project *</Label>
        <Select value={selectedProject} onValueChange={onProjectChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select project..." />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">
          Title {fieldRequired(requiredStartFields, "title") ? "*" : ""}
        </Label>
        <Input
          id="title"
          placeholder="Brief description of the bug"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">
          Description {fieldRequired(requiredStartFields, "description") ? "*" : ""}
        </Label>
        <Textarea
          id="description"
          placeholder="Detailed explanation of what's happening"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>
          Steps to Reproduce {fieldRequired(requiredStartFields, "stepsToReproduce") ? "*" : ""}
        </Label>
        <div className="space-y-2">
          {stepsToReproduce.map((step, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-6">{i + 1}.</span>
              <Input
                placeholder={`Step ${i + 1}`}
                value={step}
                onChange={(e) => updateStep(i, e.target.value)}
              />
              {stepsToReproduce.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => removeStep(i)} aria-label="Remove step">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addStep} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Step
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expected">
            Expected Behavior {fieldRequired(requiredStartFields, "expectedBehavior") ? "*" : ""}
          </Label>
          <Textarea
            id="expected"
            placeholder="What should happen"
            value={expectedBehavior}
            onChange={(e) => setExpectedBehavior(e.target.value)}
            rows={2}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="actual">
            Actual Behavior {fieldRequired(requiredStartFields, "actualBehavior") ? "*" : ""}
          </Label>
          <Textarea
            id="actual"
            placeholder="What actually happens"
            value={actualBehavior}
            onChange={(e) => setActualBehavior(e.target.value)}
            rows={2}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>
          Severity {fieldRequired(requiredStartFields, "severity") ? "*" : ""}
        </Label>
        <RadioGroup value={severity} onValueChange={(v) => setSeverity(v as RiskLevel)} className="flex gap-4">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="low" id="low" />
            <Label htmlFor="low" className="text-green-500">Low</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="medium" id="medium" />
            <Label htmlFor="medium" className="text-amber-500">Medium</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="high" id="high" />
            <Label htmlFor="high" className="text-red-500">High</Label>
          </div>
        </RadioGroup>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!isValid} className="gap-2">
          <Bug className="w-4 h-4" />
          Create Bug Report
        </Button>
      </DialogFooter>
    </div>
  );
}

function FeatureRequestForm({
  onSubmit,
  onClose,
  projects,
  selectedProject,
  onProjectChange,
  requiredStartFields,
}: FormProps & { onSubmit: (data: FeatureRequest) => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [userStory, setUserStory] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<string[]>([""]);
  const [scope, setScope] = useState<RiskLevel>("medium");
  const [constraints, setConstraints] = useState("");

  const addCriterion = () => setAcceptanceCriteria([...acceptanceCriteria, ""]);
  const removeCriterion = (index: number) => setAcceptanceCriteria(acceptanceCriteria.filter((_, i) => i !== index));
  const updateCriterion = (index: number, value: string) => {
    const newCriteria = [...acceptanceCriteria];
    newCriteria[index] = value;
    setAcceptanceCriteria(newCriteria);
  };

  const isValid =
    !!selectedProject &&
    (!fieldRequired(requiredStartFields, "title") || !!title.trim()) &&
    (!fieldRequired(requiredStartFields, "description") || !!description.trim()) &&
    (!fieldRequired(requiredStartFields, "userStory") || !!userStory.trim()) &&
    (!fieldRequired(requiredStartFields, "acceptanceCriteria") || acceptanceCriteria.some((c) => c.trim())) &&
    (!fieldRequired(requiredStartFields, "scope") || !!scope) &&
    (!fieldRequired(requiredStartFields, "constraints") || !!constraints.trim());

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      title,
      description,
      userStory,
      acceptanceCriteria: acceptanceCriteria.filter(c => c.trim()),
      scope,
      constraints: constraints || undefined,
      projectId: parseInt(selectedProject),
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Project *</Label>
        <Select value={selectedProject} onValueChange={onProjectChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select project..." />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">
          Title {fieldRequired(requiredStartFields, "title") ? "*" : ""}
        </Label>
        <Input
          id="title"
          placeholder="Brief description of the feature"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">
          Description {fieldRequired(requiredStartFields, "description") ? "*" : ""}
        </Label>
        <Textarea
          id="description"
          placeholder="What is this feature and why is it needed?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="userStory">
          User Story {fieldRequired(requiredStartFields, "userStory") ? "*" : ""}
        </Label>
        <Textarea
          id="userStory"
          placeholder="As a [user type], I want to [action] so that [benefit]"
          value={userStory}
          onChange={(e) => setUserStory(e.target.value)}
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>
          Acceptance Criteria {fieldRequired(requiredStartFields, "acceptanceCriteria") ? "*" : ""}
        </Label>
        <div className="space-y-2">
          {acceptanceCriteria.map((criterion, i) => (
            <div key={i} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={`Criterion ${i + 1}`}
                value={criterion}
                onChange={(e) => updateCriterion(i, e.target.value)}
              />
              {acceptanceCriteria.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => removeCriterion(i)} aria-label="Remove criterion">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addCriterion} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Criterion
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>
          Scope {fieldRequired(requiredStartFields, "scope") ? "*" : ""}
        </Label>
        <RadioGroup value={scope} onValueChange={(v) => setScope(v as RiskLevel)} className="flex gap-4">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="low" id="scope-low" />
            <Label htmlFor="scope-low" className="text-green-500">Small</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="medium" id="scope-medium" />
            <Label htmlFor="scope-medium" className="text-amber-500">Medium</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="high" id="scope-high" />
            <Label htmlFor="scope-high" className="text-red-500">Large</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="constraints">
          Constraints {fieldRequired(requiredStartFields, "constraints") ? "*" : "(optional)"}
        </Label>
        <Textarea
          id="constraints"
          placeholder="Any technical or business constraints to consider"
          value={constraints}
          onChange={(e) => setConstraints(e.target.value)}
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!isValid} className="gap-2">
          <Sparkles className="w-4 h-4" />
          Create Feature Request
        </Button>
      </DialogFooter>
    </div>
  );
}

function RefactorRequestForm({
  onSubmit,
  onClose,
  projects,
  selectedProject,
  onProjectChange,
  requiredStartFields,
}: FormProps & { onSubmit: (data: RefactorRequest) => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [motivation, setMotivation] = useState("");
  const [targetFiles, setTargetFiles] = useState<string[]>([""]);
  const [approach, setApproach] = useState("");
  const [riskLevel, setRiskLevel] = useState<RiskLevel>("medium");

  const addFile = () => setTargetFiles([...targetFiles, ""]);
  const removeFile = (index: number) => setTargetFiles(targetFiles.filter((_, i) => i !== index));
  const updateFile = (index: number, value: string) => {
    const newFiles = [...targetFiles];
    newFiles[index] = value;
    setTargetFiles(newFiles);
  };

  const isValid =
    !!selectedProject &&
    (!fieldRequired(requiredStartFields, "title") || !!title.trim()) &&
    (!fieldRequired(requiredStartFields, "description") || !!description.trim()) &&
    (!fieldRequired(requiredStartFields, "motivation") || !!motivation.trim()) &&
    (!fieldRequired(requiredStartFields, "targetFiles") || targetFiles.some((f) => f.trim())) &&
    (!fieldRequired(requiredStartFields, "approach") || !!approach.trim()) &&
    (!fieldRequired(requiredStartFields, "riskLevel") || !!riskLevel);

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      title,
      description,
      motivation,
      targetFiles: targetFiles.filter(f => f.trim()),
      approach,
      riskLevel,
      projectId: parseInt(selectedProject),
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Project *</Label>
        <Select value={selectedProject} onValueChange={onProjectChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select project..." />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">
          Title {fieldRequired(requiredStartFields, "title") ? "*" : ""}
        </Label>
        <Input
          id="title"
          placeholder="Brief description of the refactor"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">
          Description {fieldRequired(requiredStartFields, "description") ? "*" : ""}
        </Label>
        <Textarea
          id="description"
          placeholder="What needs to be refactored?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="motivation">
          Motivation {fieldRequired(requiredStartFields, "motivation") ? "*" : ""}
        </Label>
        <Textarea
          id="motivation"
          placeholder="Why is this refactor needed? What problems does it solve?"
          value={motivation}
          onChange={(e) => setMotivation(e.target.value)}
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>
          Target Files {fieldRequired(requiredStartFields, "targetFiles") ? "*" : ""}
        </Label>
        <div className="space-y-2">
          {targetFiles.map((file, i) => (
            <div key={i} className="flex items-center gap-2">
              <FileCode className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="src/path/to/file.ts"
                value={file}
                onChange={(e) => updateFile(i, e.target.value)}
                className="font-mono text-sm"
              />
              {targetFiles.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => removeFile(i)} aria-label="Remove file">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addFile} className="gap-2">
            <Plus className="w-4 h-4" />
            Add File
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="approach">
          Approach {fieldRequired(requiredStartFields, "approach") ? "*" : ""}
        </Label>
        <Textarea
          id="approach"
          placeholder="How should this refactor be approached? Any specific patterns or techniques?"
          value={approach}
          onChange={(e) => setApproach(e.target.value)}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>
          Risk Level {fieldRequired(requiredStartFields, "riskLevel") ? "*" : ""}
        </Label>
        <RadioGroup value={riskLevel} onValueChange={(v) => setRiskLevel(v as RiskLevel)} className="flex gap-4">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="low" id="risk-low" />
            <Label htmlFor="risk-low" className="text-green-500">Low</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="medium" id="risk-medium" />
            <Label htmlFor="risk-medium" className="text-amber-500">Medium</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="high" id="risk-high" />
            <Label htmlFor="risk-high" className="text-red-500">High</Label>
          </div>
        </RadioGroup>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!isValid} className="gap-2">
          <Wrench className="w-4 h-4" />
          Create Refactor Request
        </Button>
      </DialogFooter>
    </div>
  );
}
